<template>
  <div class="independent-wrapper">
    <div class="content">
      <div class="title">
        <span class="border"></span>
        <i class="el-icon-location-outline"></i> 内容管理 &gt; 产品资质
      </div>

      <!-- 上半区：页面头部 -->
      <div class="contentLeft section">
        <div class="section-title">页面信息</div>
        <el-form
          ref="introForm"
          :model="introData"
          :rules="introRules"
          label-width="120px"
          v-loading="introLoading"
          class="intro-form"
        >
          <el-form-item label="顶部徽章" prop="badge">
            <el-input v-model="introData.badge" placeholder="如：Certification"></el-input>
          </el-form-item>
          <el-form-item label="主标题" prop="title">
            <el-input v-model="introData.title" placeholder="如：产品资质"></el-input>
          </el-form-item>
          <el-form-item label="副标题" prop="subtitle">
            <el-input v-model="introData.subtitle" placeholder="如：权威机构认证 · 品质全程可溯"></el-input>
          </el-form-item>
          <el-form-item label="底部提示" prop="footerTip">
            <el-input v-model="introData.footerTip" placeholder="可空，留空不显示底部提示"></el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="introSaving" @click="saveIntro">保存页面信息</el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 下半区：证书列表 -->
      <div class="contentLeft section">
        <div class="section-title">证书列表</div>

        <div class="toolbar">
          <el-button class="addBtn" type="primary" @click="openAdd">新增证书</el-button>
          <el-button :disabled="!sortDirty" @click="saveSort">保存排序</el-button>
          <span v-if="sortDirty" class="hint">排序已修改，请保存</span>
        </div>

        <div class="table-con">
          <el-table
            :header-cell-style="{ background: '#f7f7f7', color: '#333' }"
            :data="certData"
            v-loading="certLoading"
            row-key="id"
            style="width: 100%"
          >
            <el-table-column align="center" label="排序" width="120">
              <template #default="{ $index }">
                <div class="sort-cell">
                  <el-button size="small" :disabled="$index === 0" @click="moveRow($index, -1)">↑</el-button>
                  <el-button size="small" :disabled="$index === certData.length - 1" @click="moveRow($index, 1)">↓</el-button>
                </div>
              </template>
            </el-table-column>
            <el-table-column align="center" label="缩略图" width="100">
              <template #default="{ row }">
                <img v-if="row.imageUrl" :src="resolveImg(row.imageUrl)" class="thumb" />
                <span v-else class="mark-thumb">{{ row.mark }}</span>
              </template>
            </el-table-column>
            <el-table-column align="center" label="标识" prop="mark" width="100"></el-table-column>
            <el-table-column align="center" label="证书全称" prop="title" show-overflow-tooltip></el-table-column>
            <el-table-column align="center" label="发证机构" prop="issueOrg" width="160" show-overflow-tooltip></el-table-column>
            <el-table-column align="center" label="颁发年" prop="issueYear" width="90"></el-table-column>
            <el-table-column align="center" label="状态" width="90">
              <template #default="{ row }">
                <span :style="{ color: row.status === 1 ? '#67c23a' : '#999' }">
                  {{ row.status === 1 ? '启用' : '隐藏' }}
                </span>
              </template>
            </el-table-column>
            <el-table-column align="center" label="操作" width="200">
              <template #default="{ row }">
                <span class="editor" @click="openEdit(row)">编辑</span>
                <span class="editor" @click="toggleCertStatus(row)">{{ row.status === 1 ? '隐藏' : '启用' }}</span>
                <el-popconfirm title="确定删除该证书?" @confirm="doDelete(row)">
                  <template #reference>
                    <span class="del">删除</span>
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <!-- 新增/编辑证书 dialog -->
    <el-dialog
      :title="(addOrEdit === 'add' ? '新增' : '编辑') + '证书'"
      v-model="dialogVisible"
      width="640px"
      center
      :close-on-click-modal="false"
    >
      <el-form ref="certForm" :model="formData" :rules="formRules" label-width="100px">
        <el-form-item label="标识" prop="mark">
          <el-input v-model="formData.mark" maxlength="10" placeholder="如：GMP / ISO / 有机"></el-input>
          <span class="hint">短标识，1-10 字，缺图时显示在色块里</span>
        </el-form-item>
        <el-form-item label="证书全称" prop="title">
          <el-input v-model="formData.title" placeholder="如：GMP 药品生产质量管理规范"></el-input>
        </el-form-item>
        <el-form-item label="简介" prop="description">
          <el-input
            type="textarea"
            :autosize="{ minRows: 2, maxRows: 4 }"
            v-model="formData.description"
            placeholder="如：生产全流程符合国家 GMP 标准，规范化管理"
          ></el-input>
        </el-form-item>
        <el-form-item label="发证机构" prop="issueOrg">
          <el-input v-model="formData.issueOrg" placeholder="如：国家药监局"></el-input>
        </el-form-item>
        <el-form-item label="颁发年份" prop="issueYear">
          <el-input v-model="formData.issueYear" maxlength="10" placeholder="如：2019"></el-input>
        </el-form-item>
        <el-form-item label="证书图" prop="imageUrl">
          <el-upload
            :show-file-list="false"
            :http-request="(o) => doUpload(o, 'imageUrl', SPECS.CERT_IMAGE)"
            accept=".png,.PNG,.jpg,.JPG,.jpeg,.JPEG"
          >
            <el-button>上传图片</el-button>
            <span class="hint">可选，留空则显示「标识」色块</span>
          </el-upload>
          <div class="upload-tip">建议 ≥600×450 PNG/JPG，≤1MB</div>
          <div v-if="formData.imageUrl" class="upload-preview">
            <img :src="resolveImg(formData.imageUrl)" />
            <span class="del-img" @click="formData.imageUrl = ''">移除</span>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submit" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import * as cmsApi from '../../api/cms'
import { SPECS, checkSpec } from '@/utils/imageSpec'

function emptyIntro() {
  return { id: 1, badge: '', title: '', subtitle: '', footerTip: '' }
}
function emptyCert() {
  return { id: '', sort: 0, status: 1, mark: '', title: '', description: '', issueOrg: '', issueYear: '', imageUrl: '' }
}

export default {
  data() {
    return {
      SPECS,
      introData: emptyIntro(),
      introLoading: false,
      introSaving: false,
      introRules: {
        title: [{ required: true, message: '请输入主标题', trigger: 'blur' }],
        subtitle: [{ required: true, message: '请输入副标题', trigger: 'blur' }],
      },

      certData: [],
      certLoading: false,
      sortDirty: false,

      dialogVisible: false,
      submitting: false,
      addOrEdit: 'add',
      formData: emptyCert(),
      formRules: {
        mark: [{ required: true, message: '请输入标识', trigger: 'blur' }],
        title: [{ required: true, message: '请输入证书全称', trigger: 'blur' }],
        issueOrg: [{ required: true, message: '请输入发证机构', trigger: 'blur' }],
        issueYear: [{ required: true, message: '请输入颁发年份', trigger: 'blur' }],
      },
    }
  },
  mounted() {
    this.fetchIntro()
    this.fetchCertList()
  },
  methods: {
    resolveImg(url) {
      if (!url) return ''
      if (/^https?:\/\//.test(url)) return url
      if (url.startsWith('/')) return cmsApi.imageBaseUrl.replace(/\/$/, '') + url
      return cmsApi.imageBaseUrl + url
    },

    async fetchIntro() {
      this.introLoading = true
      try {
        const res = await cmsApi.qualIntroGet()
        if (res.data && res.data.code === 0) {
          this.introData = { ...emptyIntro(), ...(res.data.data || {}) }
        } else {
          this.$message.error(res.data?.msg || '加载页面信息失败')
        }
      } catch (e) {
        this.$message.error('网络异常')
      } finally {
        this.introLoading = false
      }
    },
    saveIntro() {
      this.$refs.introForm.validate(async (valid) => {
        if (!valid) return
        this.introSaving = true
        try {
          const res = await cmsApi.qualIntroEdit({ ...this.introData, id: 1 })
          if (res.data && res.data.code === 0) {
            this.$message.success('保存成功')
            this.fetchIntro()
          } else {
            this.$message.error(res.data?.msg || '保存失败')
          }
        } catch (e) {
          this.$message.error('网络异常')
        } finally {
          this.introSaving = false
        }
      })
    },

    async fetchCertList() {
      this.certLoading = true
      this.sortDirty = false
      try {
        const res = await cmsApi.qualCertList()
        if (res.data && res.data.code === 0) {
          this.certData = (res.data.data || []).slice().sort((a, b) => (a.sort || 0) - (b.sort || 0))
        } else {
          this.$message.error(res.data?.msg || '加载失败')
        }
      } catch (e) {
        this.$message.error('网络异常')
      } finally {
        this.certLoading = false
      }
    },
    moveRow(index, dir) {
      const target = index + dir
      if (target < 0 || target >= this.certData.length) return
      const arr = this.certData.slice()
      const tmp = arr[index]
      arr[index] = arr[target]
      arr[target] = tmp
      this.certData = arr
      this.sortDirty = true
    },
    async saveSort() {
      const items = this.certData.map((row, idx) => ({ id: row.id, sort: idx + 1 }))
      try {
        const res = await cmsApi.qualCertSort(items)
        if (res.data && res.data.code === 0) {
          this.$message.success('排序已保存')
          this.fetchCertList()
        } else {
          this.$message.error(res.data?.msg || '保存失败')
        }
      } catch (e) {
        this.$message.error('网络异常')
      }
    },

    openAdd() {
      this.addOrEdit = 'add'
      this.formData = emptyCert()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.certForm?.clearValidate())
    },
    openEdit(row) {
      this.addOrEdit = 'edit'
      this.formData = { ...emptyCert(), ...row }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.certForm?.clearValidate())
    },
    async toggleCertStatus(row) {
      const next = row.status === 1 ? 0 : 1
      try {
        const res = await cmsApi.qualCertToggle(row.id, next)
        if (res.data && res.data.code === 0) {
          this.$message.success(next === 1 ? '已启用' : '已隐藏')
          this.fetchCertList()
        } else {
          this.$message.error(res.data?.msg || '操作失败')
        }
      } catch (e) {
        this.$message.error('网络异常')
      }
    },
    async doDelete(row) {
      try {
        const res = await cmsApi.qualCertDelete(row.id)
        if (res.data && res.data.code === 0) {
          this.$message.success('删除成功')
          this.fetchCertList()
        } else {
          this.$message.error(res.data?.msg || '删除失败')
        }
      } catch (e) {
        this.$message.error('网络异常')
      }
    },
    async doUpload(option, field, spec = { maxKB: 10240 }) {
      const file = option.file
      if (!file) return
      if (!(await checkSpec(this, file, spec))) return
      try {
        const res = await cmsApi.uploadImage(file)
        const path = res.data?.data || res.data?.msg
        if (res.data && res.data.code === 0 && path) {
          this.formData[field] = path
          this.$message.success('上传成功')
        } else {
          this.$message.error(res.data?.msg || '上传失败')
        }
      } catch (e) {
        this.$message.error('上传失败')
      }
    },
    submit() {
      this.$refs.certForm.validate(async (valid) => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = { ...this.formData }
          if (this.addOrEdit === 'add') delete payload.id
          const res = this.addOrEdit === 'add'
            ? await cmsApi.qualCertAdd(payload)
            : await cmsApi.qualCertEdit(payload)
          if (res.data && res.data.code === 0) {
            this.$message.success(this.addOrEdit === 'add' ? '新增成功' : '编辑成功')
            this.dialogVisible = false
            this.fetchCertList()
          } else {
            this.$message.error(res.data?.msg || '操作失败')
          }
        } catch (e) {
          this.$message.error('网络异常')
        } finally {
          this.submitting = false
        }
      })
    },
  },
}
</script>

<style lang="less" scoped>
@import "../../../static/css/layout.less";

.independent-wrapper {
  width: 100%;
  height: 100%;
  overflow-y: auto;
}
.content {
  padding: 20px;
  .title {
    font-size: 14px;
    color: #333;
    margin-bottom: 20px;
    .border {
      display: inline-block;
      width: 3px;
      height: 14px;
      background: #679ff6;
      margin-right: 8px;
      vertical-align: middle;
    }
  }
}
.section {
  background: #fff;
  padding: 16px 20px 20px;
  margin-bottom: 20px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}
.intro-form {
  max-width: 720px;
}
.toolbar {
  margin-bottom: 12px;
  .addBtn {
    background: #679ff6;
    border-color: #679ff6;
    color: #fff;
  }
  .hint {
    margin-left: 12px;
    color: #e6a23c;
    font-size: 12px;
  }
}
.table-con {
  float: none !important;
  .thumb {
    width: 36px;
    height: 36px;
    object-fit: contain;
    background: #f7f7f7;
    border-radius: 4px;
  }
  .mark-thumb {
    display: inline-block;
    min-width: 36px;
    padding: 4px 8px;
    background: #ecf5ff;
    color: #409eff;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
  }
  .sort-cell {
    display: flex;
    justify-content: center;
    gap: 4px;
  }
  .editor {
    color: #679ff6;
    cursor: pointer;
    margin-right: 10px;
  }
  .del {
    color: #f56c6c;
    cursor: pointer;
  }
}
.upload-preview {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  img {
    max-width: 120px;
    max-height: 80px;
    border: 1px solid #eee;
    border-radius: 4px;
  }
  .del-img {
    color: #f56c6c;
    cursor: pointer;
    font-size: 12px;
  }
}
.hint {
  color: #999;
  font-size: 12px;
  margin-left: 12px;
}
.upload-tip {
  color: #909399;
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.5;
}
</style>
